import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { invokeUnsubscribe } from "@/api/messaging";
import { Button } from "@/components/ui/button";

type Status = "confirm" | "loading" | "success" | "already" | "error" | "invalid";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("e") ?? "";
  const token = searchParams.get("t") ?? "";
  // Never opt out on mount: mail-security scanners / link-preview bots that
  // execute the page would otherwise unsubscribe the recipient without consent.
  // We only POST from an explicit button click. Missing params => invalid.
  const [status, setStatus] = useState<Status>(email && token ? "confirm" : "invalid");

  const confirmUnsubscribe = async () => {
    setStatus("loading");
    try {
      const { data, error } = await invokeUnsubscribe(email, token);
      if (error || !data?.ok) {
        setStatus("error");
        return;
      }
      setStatus(data.alreadyUnsubscribed ? "already" : "success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 max-w-md text-center">
        {status === "confirm" && (
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-xl font-bold">Unsubscribe from Quizrun emails?</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {email && <span className="font-medium">{email}</span>} will stop
              receiving Quizrun newsletters and announcements. You'll still get
              essential emails about events you've registered for (registration
              confirmations and your results).
            </p>
            <Button onClick={confirmUnsubscribe}>Unsubscribe</Button>
          </div>
        )}

        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p>Updating your email preferences…</p>
          </div>
        )}

        {(status === "success" || status === "already") && (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
            <h1 className="text-xl font-bold">
              {status === "already" ? "You're already unsubscribed" : "You've been unsubscribed"}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {email && <span className="font-medium">{email}</span>} won't receive
              Quizrun newsletters or announcements anymore. You'll still get
              essential emails about events you've registered for (registration
              confirmations and your results).
            </p>
            <p className="text-xs text-muted-foreground">
              Changed your mind? Just ask us and we'll add you back.
            </p>
          </div>
        )}

        {status === "invalid" && (
          <div className="flex flex-col items-center gap-3">
            <XCircle className="w-10 h-10 text-red-500" />
            <h1 className="text-xl font-bold">Invalid unsubscribe link</h1>
            <p className="text-sm text-muted-foreground">
              This link is missing information. Please use the unsubscribe link
              from the bottom of one of our emails.
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-3">
            <XCircle className="w-10 h-10 text-red-500" />
            <h1 className="text-xl font-bold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              We couldn't process your unsubscribe request. Please try the link
              again, or contact us and we'll sort it out.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
